import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Plus, Trash2, Calendar, Save } from "lucide-react";
import { handleGet, handleRemove, handleSend, handleUpdate } from "../../../controllers/controller.all";

export default function CoefficientList() {
    const user = useSelector((state) => state.userReducer?.user);
    const token = user?.access_token || "";
    const ecoleId = user?.ecole?._id || user?.ecole;

    const [items, setItems] = useState([]);
    const [classes, setClasses] = useState([]);
    const [matieres, setMatieres] = useState([]);
    const [annees, setAnnees] = useState([]);

    const [selectedAnnee, setSelectedAnnee] = useState("");
    const [selectedClasse, setSelectedClasse] = useState("");
    const [loading, setLoading] = useState(true);

    // Form state
    const [showForm, setShowForm] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [formData, setFormData] = useState({
        classe: "",
        matiere: "",
        valeur: 1
    });

    const fetchData = async () => {
        if (!ecoleId || !token) return;
        setLoading(true);

        // Fetch classes
        await handleGet(`/classe/ecole/${ecoleId}`, (res) => {
            if (res?.classes) setClasses(res.classes);
        }, token);

        // Fetch matières
        await handleGet(`/matiere/ecole/${ecoleId}`, (res) => {
            if (res?.matieres) setMatieres(res.matieres);
        }, token);

        // Fetch années
        await handleGet(`/ecole-annee/ecole/${ecoleId}`, (res) => {
            if (res?.annees) setAnnees(res.annees);
        }, token);

        setLoading(false);
    };

    const fetchCoefficients = async () => {
        if (!ecoleId || !token) return;
        const query = selectedAnnee ? `?annee=${selectedAnnee}` : "";
        await handleGet(`/coefficient/ecole/${ecoleId}${query}`, (res) => {
            if (res?.coefficients) setItems(res.coefficients);
        }, token);
    };

    useEffect(() => {
        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ecoleId, token]);

    useEffect(() => {
        fetchCoefficients();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedAnnee, ecoleId, token]);

    const handleDelete = async (id) => {
        await handleRemove(`/coefficient/${id}`, fetchCoefficients, token);
    };

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        // Le backend va gérer l'année par défaut si on n'envoie rien ou l'année qu'on passe
        const payload = {
            ...formData,
            ecole: ecoleId,
            valeur: parseInt(formData.valeur, 10),
            annee: selectedAnnee || "" // Si selectedAnnee est vide, le backend prendra l'année active
        };

        let res;
        if (editingId) {
            res = await handleUpdate(e, `/coefficient/${editingId}`, payload, null, token);
        } else {
            res = await handleSend(e, "/coefficient", payload, null, token);
        }

        if (res && (res.state === "success" || res.status === "success")) {
            setShowForm(false);
            setEditingId(null);
            setFormData({ classe: "", matiere: "", valeur: 1 });
            fetchCoefficients();
        }
    };

    // Helper functions to get names
    const getClasseName = (id) => {
        const c = classes.find(cls => cls._id === id);
        return c ? c.libelle : "Inconnue";
    };

    const getMatiereName = (id) => {
        const m = matieres.find(mat => mat._id === id);
        return m ? m.libelle : "Inconnue";
    };

    // Filter items by selected class in the table if a class filter is applied
    const filteredItems = selectedClasse
        ? items.filter(i => i.classe === selectedClasse)
        : items;

    return (
        <div className="flex flex-col gap-6">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900 tracking-tight">Coefficients</h1>
                    <p className="text-sm text-gray-500 mt-1">Définissez les coefficients des matières par classe.</p>
                </div>
                <button
                    onClick={() => {
                        setShowForm(!showForm);
                        if (showForm) setEditingId(null);
                        setFormData({ classe: "", matiere: "", valeur: 1 });
                    }}
                    disabled={loading || (annees.length === 0)}
                    className="inline-flex items-center gap-2 bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 rounded-lg transition disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    {showForm ? "Fermer" : <><Plus className="h-4 w-4" /> Ajouter un Coefficient</>}
                </button>
            </div>

            {!loading && annees.length === 0 && (
                <div className="bg-orange-50 border border-orange-200 text-orange-800 p-4 rounded-xl flex items-start gap-3">
                    <svg className="w-5 h-5 shrink-0 mt-0.5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"></circle><line x1="12" y1="8" x2="12" y2="12"></line><line x1="12" y1="16" x2="12.01" y2="16"></line></svg>
                    <div>
                        <h3 className="font-semibold">Aucune année scolaire active !</h3>
                        <p className="text-sm mt-1">Vous devez d'abord créer et activer une année scolaire avant de pouvoir ajouter des coefficients.</p>
                        <a href="/annees" className="inline-block mt-3 text-sm font-medium text-orange-800 bg-orange-100 hover:bg-orange-200 px-3 py-1.5 rounded-lg transition">
                            Aller à la gestion des années scolaires
                        </a>
                    </div>
                </div>
            )}

            {showForm && (
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-4">
                    <h2 className="text-lg font-medium text-gray-900 mb-4">Ajouter / Modifier un coefficient</h2>
                    <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-4 gap-6">
                        <div className="space-y-2">
                            <label className="text-sm font-medium text-gray-700">Classe *</label>
                            <select
                                required
                                name="classe"
                                value={formData.classe}
                                onChange={handleChange}
                                className="w-full border border-gray-300 rounded-lg py-2 px-3 focus:ring-2 focus:ring-indigo-500 outline-none"
                            >
                                <option value="">Choisir une classe</option>
                                {classes.map(c => (
                                    <option key={c._id} value={c._id}>{c.libelle}</option>
                                ))}
                            </select>
                        </div>
                        <div className="space-y-2">
                            <label className="text-sm font-medium text-gray-700">Matière *</label>
                            <select
                                required
                                name="matiere"
                                value={formData.matiere}
                                onChange={handleChange}
                                className="w-full border border-gray-300 rounded-lg py-2 px-3 focus:ring-2 focus:ring-indigo-500 outline-none"
                            >
                                <option value="">Choisir une matière</option>
                                {matieres.map(m => (
                                    <option key={m._id} value={m._id}>{m.libelle}</option>
                                ))}
                            </select>
                        </div>
                        <div className="space-y-2">
                            <label className="text-sm font-medium text-gray-700">Valeur (Coefficient) *</label>
                            <input
                                required
                                type="number"
                                min="1"
                                name="valeur"
                                value={formData.valeur}
                                onChange={handleChange}
                                className="w-full border border-gray-300 rounded-lg py-2 px-3 focus:ring-2 focus:ring-indigo-500 outline-none"
                            />
                        </div>
                        <div className="flex items-end">
                            <button type="submit" className="w-full inline-flex justify-center items-center gap-2 bg-indigo-600 px-6 py-2.5 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 rounded-lg transition">
                                <Save className="h-4 w-4" /> {editingId ? "Mettre à jour" : "Enregistrer"}
                            </button>
                        </div>
                    </form>
                </div>
            )}

            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 mb-2">
                <div className="flex flex-wrap gap-4 items-end">
                    <div className="flex flex-col gap-1.5 min-w-[200px]">
                        <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide flex items-center gap-1.5">
                            <Calendar className="w-3.5 h-3.5" /> Année Scolaire
                        </label>
                        <select
                            value={selectedAnnee}
                            onChange={(e) => setSelectedAnnee(e.target.value)}
                            className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        >
                            <option value="">Année Active</option>
                            {annees.map((a) => (
                                <option key={a._id} value={a.annee}>{a.annee}</option>
                            ))}
                        </select>
                    </div>

                    <div className="flex flex-col gap-1.5 min-w-[200px]">
                        <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide flex items-center gap-1.5">
                            Filtre Classe
                        </label>
                        <select
                            value={selectedClasse}
                            onChange={(e) => setSelectedClasse(e.target.value)}
                            className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        >
                            <option value="">Toutes les classes</option>
                            {classes.map((c) => (
                                <option key={c._id} value={c._id}>{c.libelle}</option>
                            ))}
                        </select>
                    </div>
                </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Classe</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Matière</th>
                                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Coefficient</th>
                                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {loading ? (
                                <tr>
                                    <td colSpan="4" className="px-6 py-10 text-center text-sm text-gray-500">Chargement...</td>
                                </tr>
                            ) : filteredItems.length === 0 ? (
                                <tr>
                                    <td colSpan="4" className="px-6 py-10 text-center text-sm text-gray-500">Aucun coefficient défini.</td>
                                </tr>
                            ) : (
                                filteredItems.map((item) => (
                                    <tr key={item._id} className="hover:bg-gray-50 transition">
                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                            {getClasseName(item.classe)}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                                            {getMatiereName(item.matiere)}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-center">
                                            <span className="inline-flex items-center justify-center px-3 py-1 rounded-full text-sm font-bold bg-indigo-100 text-indigo-800">
                                                x {item.valeur}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                            <div className="flex items-center justify-end gap-2">
                                                <button
                                                    onClick={() => {
                                                        setEditingId(item._id);
                                                        setFormData({
                                                            classe: item.classe,
                                                            matiere: item.matiere,
                                                            valeur: item.valeur
                                                        });
                                                        setShowForm(true);
                                                    }}
                                                    className="text-indigo-600 hover:text-indigo-900 bg-indigo-50 p-2 rounded-lg transition"
                                                    title="Modifier"
                                                >
                                                    <svg className="w-4 h-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z"></path></svg>
                                                </button>
                                                <button
                                                    onClick={() => handleDelete(item._id)}
                                                    className="text-red-500 hover:text-red-700 bg-red-50 p-2 rounded-lg transition"
                                                    title="Supprimer"
                                                >
                                                    <Trash2 className="h-4 w-4" />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}
